import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowDownToLine, ArrowUpFromLine, Copy, Check, Clock, CheckCircle2,
  XCircle, Loader2, MoreHorizontal, Eye
} from "lucide-react";
import { format } from "date-fns";
import { th, enUS } from "date-fns/locale";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { RowTimer } from "../utilities/rowtimer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";

const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;700&display=swap');

.tt-wrap { font-family: 'DM Sans', sans-serif; color: #d9edf8; }
.tt-wrap * { box-sizing: border-box; }

/* ── CARD ── */
.tt-card {
  background: linear-gradient(160deg, #070f1e 0%, #091525 60%, #0a1828 100%);
  border: 1px solid rgba(34,211,238,0.10);
  border-radius: 18px; overflow: hidden;
  box-shadow: 0 4px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(34,211,238,0.04);
  position: relative;
  width: 100%;
}
.tt-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, transparent 0%, rgba(34,211,238,0.5) 35%, rgba(56,189,248,0.7) 50%, rgba(34,211,238,0.5) 65%, transparent 100%);
  z-index: 3;
}

/* ── TABLE ── */
.tt-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.tt-thead th {
  text-align: left; padding: 13px 14px;
  font-size: 0.62rem; font-weight: 800; color: #2a4f6e;
  text-transform: uppercase; letter-spacing: 0.12em;
  background: rgba(0,0,0,0.3);
  border-bottom: 1px solid rgba(34,211,238,0.06);
  white-space: nowrap;
  overflow: hidden;
}
.tt-thead th.r { text-align: right; }

/* ── ROW ── */
.tt-tr {
  border-bottom: 1px solid rgba(34,211,238,0.04);
  cursor: pointer; position: relative;
  transition: background 0.18s;
}
.tt-tr:last-child { border-bottom: none; }
.tt-tr:hover { background: rgba(34,211,238,0.022); }

/* accent bar via td pseudo — avoids invalid div-in-tr */
.tt-tr td:first-child { position: relative; }
.tt-tr td:first-child::before {
  content: '';
  position: absolute;
  left: 0; top: 6px; bottom: 6px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: linear-gradient(180deg, #22d3ee, #0ea5e9);
  box-shadow: 0 0 10px rgba(34,211,238,0.7);
  opacity: 0;
  transition: opacity 0.2s;
}
.tt-tr:hover td:first-child::before { opacity: 1; }

.tt-td {
  padding: 13px 14px;
  vertical-align: middle;
  overflow: hidden;
}
.tt-td.r { text-align: right; }

/* ── TYPE ICON ── */
.tt-icon {
  width: 34px; height: 34px; border-radius: 11px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  position: relative; overflow: hidden;
  transition: box-shadow 0.25s, transform 0.2s;
}
.tt-icon::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 44%; background: rgba(255,255,255,0.1); }
.tt-tr:hover .tt-icon { transform: scale(1.08) rotate(-4deg); }
.tt-icon svg { width: 15px; height: 15px; position: relative; z-index: 1; }
.tt-dep { background: linear-gradient(135deg,rgba(14,165,233,0.2),rgba(14,165,233,0.07)); border: 1px solid rgba(14,165,233,0.22); }
.tt-dep svg { color: #38bdf8; }
.tt-tr:hover .tt-dep { box-shadow: 0 0 16px rgba(14,165,233,0.35); }
.tt-with { background: linear-gradient(135deg,rgba(34,211,238,0.15),rgba(34,211,238,0.05)); border: 1px solid rgba(34,211,238,0.22); }
.tt-with svg { color: #22d3ee; }
.tt-tr:hover .tt-with { box-shadow: 0 0 16px rgba(34,211,238,0.35); }

/* ── TX ID ── */
.tt-txid {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.75rem; color: #e0eeff; font-weight: 600;
  transition: color 0.18s;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 120px; display: inline-block; vertical-align: middle;
}
.tt-tr:hover .tt-txid { color: #fff; }
.tt-chip { display: inline-block; margin-top: 4px; padding: 2px 7px; border-radius: 6px; font-size: 0.58rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.07em; }
.tt-chip-dep { background: rgba(14,165,233,0.1); color: #38bdf8; border: 1px solid rgba(14,165,233,0.2); }
.tt-chip-with { background: rgba(34,211,238,0.08); color: #22d3ee; border: 1px solid rgba(34,211,238,0.2); }

.tt-copy { background: none; border: none; cursor: pointer; color: #2a4f6e; padding: 3px; line-height: 0; border-radius: 5px; transition: color 0.18s, transform 0.18s; flex-shrink: 0; }
.tt-copy:hover { color: #22d3ee; transform: scale(1.2); }
.tt-copy svg { width: 12px; height: 12px; }

/* ── PROVIDER ── */
.tt-prov {
  font-size: 0.82rem; font-weight: 700; color: #4a7a94;
  transition: color 0.18s;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  display: block;
}
.tt-tr:hover .tt-prov { color: #7aaec8; }

/* ── AMOUNT ── */
.tt-amt {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem; font-weight: 800;
  transition: text-shadow 0.22s;
  white-space: nowrap;
  display: block;
}
.tt-amt-dep { color: #38bdf8; }
.tt-amt-with { color: #22d3ee; }
.tt-tr:hover .tt-amt-dep { text-shadow: 0 0 16px rgba(56,189,248,0.45); }
.tt-tr:hover .tt-amt-with { text-shadow: 0 0 16px rgba(34,211,238,0.45); }

/* ── STATUS BADGE ── */
.tt-badge { display: inline-flex; align-items: center; gap: 5px; border-radius: 999px; padding: 4px 10px; font-size: 0.67rem; font-weight: 800; white-space: nowrap; transition: box-shadow 0.22s, filter 0.22s; }
.tt-badge svg { width: 10px; height: 10px; }
.tt-ok  { background: rgba(16,185,129,0.1);  color: #34d399; border: 1px solid rgba(16,185,129,0.25); }
.tt-pnd { background: rgba(245,158,11,0.1);  color: #fbbf24; border: 1px solid rgba(245,158,11,0.22); }
.tt-fail{ background: rgba(239,68,68,0.1);   color: #f87171; border: 1px solid rgba(239,68,68,0.22); }
.tt-proc{ background: rgba(14,165,233,0.1);  color: #38bdf8; border: 1px solid rgba(14,165,233,0.22); }
.tt-tr:hover .tt-ok   { box-shadow: 0 0 12px rgba(16,185,129,0.25); filter: brightness(1.1); }
.tt-tr:hover .tt-fail  { box-shadow: 0 0 12px rgba(239,68,68,0.2);  filter: brightness(1.1); }
.tt-tr:hover .tt-proc  { box-shadow: 0 0 12px rgba(14,165,233,0.25); filter: brightness(1.1); }
.tt-tr:hover .tt-pnd   { box-shadow: 0 0 12px rgba(245,158,11,0.2);  filter: brightness(1.1); }
@keyframes tt-spin { to { transform: rotate(360deg); } }
.tt-spin { animation: tt-spin 0.85s linear infinite; display: inline-block; }

/* ── DATE ── */
.tt-date {
  color: #4a7a94; font-size: 0.74rem;
  font-family: 'JetBrains Mono', monospace;
  white-space: nowrap; transition: color 0.18s;
  display: block;
}
.tt-tr:hover .tt-date { color: #7aaec8; }
.tt-timer { margin-top: 2px; }

/* ── ACTIONS ── */
.tt-action-btn {
  width: 32px; height: 32px; border-radius: 9px;
  background: rgba(34,211,238,0.06); border: 1px solid rgba(34,211,238,0.14);
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  transition: all 0.2s; color: #22d3ee;
}
.tt-action-btn:hover { background: rgba(34,211,238,0.12); border-color: rgba(34,211,238,0.3); transform: scale(1.08); box-shadow: 0 0 12px rgba(34,211,238,0.18); }
.tt-action-btn svg { width: 14px; height: 14px; }

.tt-dropdown-content {
  background: #070f1e !important; border: 1px solid rgba(34,211,238,0.12) !important;
  border-radius: 12px !important; box-shadow: 0 8px 32px rgba(0,0,0,0.5) !important;
  padding: 5px !important; min-width: 150px !important;
}
.tt-dropdown-item {
  display: flex; align-items: center; gap: 8px; padding: 8px 10px;
  font-size: 0.8rem; color: #7aaec8 !important; border-radius: 8px;
  cursor: pointer; transition: all 0.2s !important; outline: none;
  font-family: 'DM Sans', sans-serif; font-weight: 600;
}
.tt-dropdown-item:hover, .tt-dropdown-item:focus { background: rgba(34,211,238,0.08) !important; color: #22d3ee !important; }
.tt-dropdown-icon { width: 14px; height: 14px; }

/* ── SKELETON ── */
.tt-skel { background: rgba(34,211,238,0.04); border-radius: 8px; animation: tt-pulse 1.8s ease-in-out infinite; }
@keyframes tt-pulse { 0%,100%{opacity:.25}50%{opacity:.7} }
.tt-srow { display: flex; align-items: center; gap: 12px; padding: 13px 16px; border-bottom: 1px solid rgba(34,211,238,0.04); }
.tt-scirc { width: 36px; height: 36px; border-radius: 11px; flex-shrink: 0; }
.tt-sline { border-radius: 6px; }

/* ── EMPTY ── */
.tt-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 70px 20px; gap: 14px; }
.tt-empty-ico { width: 68px; height: 68px; border-radius: 20px; background: rgba(34,211,238,0.04); border: 1px solid rgba(34,211,238,0.1); display: flex; align-items: center; justify-content: center; }
.tt-empty-ico svg { width: 26px; height: 26px; color: #1a3a52; }
.tt-empty-txt { color: #2a4f6e; font-size: 0.9rem; font-weight: 600; }

/* ════ MOBILE CARD VIEW ════ */
.tt-mcard {
  padding: 14px 15px;
  border-bottom: 1px solid rgba(34,211,238,0.05);
  cursor: pointer; position: relative;
  transition: background 0.18s;
}
.tt-mcard:last-child { border-bottom: none; }
.tt-mcard:hover { background: rgba(34,211,238,0.025); }
.tt-mcard .tt-accent { opacity: 0; transition: opacity 0.2s; }
.tt-mcard:hover .tt-accent { opacity: 1; }
.tt-accent { position: absolute; left: 0; top: 5px; bottom: 5px; width: 3px; border-radius: 0 3px 3px 0; background: linear-gradient(180deg, #22d3ee, #0ea5e9); box-shadow: 0 0 10px rgba(34,211,238,0.7); }

/* Mobile card top row */
.tt-mc-top { display: flex; align-items: center; gap: 11px; margin-bottom: 10px; }
.tt-mc-info { flex: 1; min-width: 0; }
.tt-mc-txid { font-family: 'JetBrains Mono', monospace; font-size: 0.78rem; color: #e0eeff; font-weight: 600; display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
.tt-mc-meta { display: flex; align-items: center; gap: 7px; flex-wrap: wrap; }

/* Mobile card bottom row */
.tt-mc-bot { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.tt-mc-amt-block { display: flex; flex-direction: column; gap: 2px; }
.tt-mc-amt-lbl { font-size: 0.58rem; font-weight: 700; color: #2a4f6e; text-transform: uppercase; letter-spacing: 0.1em; }
.tt-mc-right { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }

/* Desktop only */
@media (max-width: 767px) { .tt-desktop-only { display: none !important; } }
@media (min-width: 768px) { .tt-mobile-only { display: none !important; } }
`;

const STATUS_CFG = {
  success: { icon: CheckCircle2, cls: "tt-ok", label: "Success" },
  pending: { icon: Clock, cls: "tt-pnd", label: "Pending" },
  failed: { icon: XCircle, cls: "tt-fail", label: "Failed" },
  processing: { icon: Loader2, cls: "tt-proc", label: "Processing", spin: true },
};

function StatusBadge({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.pending;
  const Icon = c.icon;
  return (
    <span className={`tt-badge ${c.cls}`}>
      <Icon className={c.spin ? "tt-spin" : ""} />{c.label}
    </span>
  );
}

function SkeletonRows({ count = 5 }) {
  return Array.from({ length: count }).map((_, i) => (
    <div key={i} className="tt-srow">
      <div className="tt-skel tt-scirc" />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
        <div className="tt-skel tt-sline" style={{ height: 12, width: "38%" }} />
        <div className="tt-skel tt-sline" style={{ height: 9, width: "22%" }} />
      </div>
      <div className="tt-skel tt-sline" style={{ height: 12, width: "15%", marginRight: 12 }} />
      <div className="tt-skel tt-sline" style={{ height: 24, width: "18%", borderRadius: 99 }} />
    </div>
  ));
}

export default function TransactionTable({ transactions, isLoading, onRowClick, compact = false, selectedId = null }) {
  let t = null;
  let language = "en";
  try { const lc = useLanguage(); t = lc?.t; language = lc?.language || "en"; } catch (_) { }
  const tr = (key, fb) => t ? t(key) : (fb || key);
  const [copiedId, setCopiedId] = React.useState(null);

  const copy = (e, id) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2200);
  };

  const fmtDate = (str) => {
    if (!str) return "—";
    try {
      const d = new Date(str.includes("T") ? str : str.replace(" ", "T") + "Z");
      return format(d, language === "th" ? "d MMM, HH:mm" : "MMM d, HH:mm", { locale: language === "th" ? th : enUS });
    } catch { return "—"; }
  };

  if (isLoading) {
    return (
      <div className="tt-wrap">
        <style dangerouslySetInnerHTML={{ __html: STYLE }} />
        <div className="tt-card">
          <div className="tt-desktop-only">
            <table className="tt-table">
              <thead className="tt-thead"><tr>
                <th style={{ width: "32%" }}>{tr("transaction", "Transaction")}</th>
                <th style={{ width: "15%" }}>{tr("provider", "Provider")}</th>
                <th className="r" style={{ width: "18%" }}>{tr("amount", "Amount")}</th>
                <th style={{ width: "15%" }}>{tr("status", "Status")}</th>
                <th className="r" style={{ width: "15%" }}>{tr("date", "Date")}</th>
                <th style={{ width: "5%" }} />
              </tr></thead>
            </table>
          </div>
          <SkeletonRows />
        </div>
      </div>
    );
  }

  if (!transactions || transactions.length === 0) {
    return (
      <div className="tt-wrap">
        <style dangerouslySetInnerHTML={{ __html: STYLE }} />
        <div className="tt-card">
          <motion.div className="tt-empty" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <div className="tt-empty-ico">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V19.5A2.25 2.25 0 0010.5 21.75h6.75A2.25 2.25 0 0019.5 19.5V6.108z" />
              </svg>
            </div>
            <p className="tt-empty-txt">{tr("noTransactionsFound", "No transactions found")}</p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="tt-wrap">
      <style dangerouslySetInnerHTML={{ __html: STYLE }} />
      <div className="tt-card">

        {/* ══ DESKTOP TABLE VIEW ══ */}
        <div className="tt-desktop-only">
          <table className="tt-table">
            {!compact && (
              <thead className="tt-thead">
                <tr>
                  {/* Col widths: total = 100% */}
                  <th style={{ width: "32%" }}>{tr("transaction", "Transaction")}</th>
                  <th style={{ width: "15%" }}>{tr("provider", "Provider")}</th>
                  <th className="r" style={{ width: "18%" }}>{tr("netAmount", "Net Amount")}</th>
                  <th className="r" style={{ width: "18%" }}>{tr("totalFee", "Total Fee")}</th>
                  <th style={{ width: "15%" }}>{tr("status", "Status")}</th>
                  <th className="r" style={{ width: "15%" }}>{tr("date", "Date")}</th>
                  <th style={{ width: "5%" }} />
                </tr>
              </thead>
            )}
            <tbody>
              {transactions.map((tx, i) => (
                <motion.tr
                  key={tx.id || i}
                  className={`tt-tr${selectedId === tx.id ? " tt-selected" : ""}`}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.016, duration: 0.26 }}
                  onClick={() => onRowClick?.(tx)}
                >
                  {/* Transaction — accent via CSS ::before on first td */}
                  <td className="tt-td" style={{ paddingLeft: 18 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div className={`tt-icon ${tx.type === "deposit" ? "tt-dep" : "tt-with"}`}>
                        {tx.type === "deposit" ? <ArrowDownToLine /> : <ArrowUpFromLine />}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span className="tt-txid">{tx.transaction_id?.slice(0, 14)}…</span>
                          <button className="tt-copy" onClick={(e) => copy(e, tx.transaction_id)}>
                            {copiedId === tx.transaction_id
                              ? <Check style={{ width: 12, height: 12, color: "#34d399" }} />
                              : <Copy />}
                          </button>
                        </div>
                        <span className={`tt-chip ${tx.type === "deposit" ? "tt-chip-dep" : "tt-chip-with"}`}>
                          {tx.type}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Provider */}
                  <td className="tt-td">
                    <span className="tt-prov">{tx.provider_display || tx.provider_code || "—"}</span>
                  </td>

                  {/* Amount */}
                  <td className="tt-td r">
                    <span className={`tt-amt ${tx.type === "deposit" ? "tt-amt-dep" : "tt-amt-with"}`}>
                      {tx.type === "deposit" ? "+" : "−"}฿{tx.net_amount ?? "0"}
                    </span>
                  </td>

                  {/* Total Fee */}
                  <td className="tt-td r">
                    <span className={`tt-fee ${tx.type === "deposit" ? "tt-fee-dep" : "tt-fee-with"}`}>
                      ฿{tx.total_fee ?? "0"}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="tt-td">
                    <StatusBadge status={tx.status} />
                  </td>

                  {/* Date */}
                  <td className="tt-td r">
                    <span className="tt-date">{fmtDate(tx.created_date)}</span>
                    <div className="tt-timer"><RowTimer createdAt={tx.created_date} /></div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ══ MOBILE CARD VIEW ══ */}
        <div className="tt-mobile-only">
          {transactions.map((tx, i) => (
            <motion.div
              key={tx.id || i}
              className="tt-mcard"
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.016, duration: 0.26 }}
              onClick={() => onRowClick?.(tx)}
            >
              <div className="tt-accent" />
              {/* Top row: icon + tx id + status */}
              <div className="tt-mc-top">
                <div className={`tt-icon ${tx.type === "deposit" ? "tt-dep" : "tt-with"}`}>
                  {tx.type === "deposit" ? <ArrowDownToLine /> : <ArrowUpFromLine />}
                </div>
                <div className="tt-mc-info">
                  <div className="tt-mc-txid">
                    <span>{tx.transaction_id?.slice(0, 16)}…</span>
                    <button className="tt-copy" onClick={(e) => copy(e, tx.transaction_id)}>
                      {copiedId === tx.transaction_id
                        ? <Check style={{ width: 12, height: 12, color: "#34d399" }} />
                        : <Copy />}
                    </button>
                  </div>
                  <div className="tt-mc-meta">
                    <span className={`tt-chip ${tx.type === "deposit" ? "tt-chip-dep" : "tt-chip-with"}`}>{tx.type}</span>
                    {(tx.provider_display || tx.provider_code) && (
                      <span style={{ fontSize: "0.72rem", color: "#4a7a94", fontWeight: 600 }}>
                        {tx.provider_display || tx.provider_code}
                      </span>
                    )}
                  </div>
                </div>

              </div>
              {/* Bottom row: amount + date */}
              <div className="tt-mc-bot">
                <div className="tt-mc-amt-block">
                  <div className="tt-mc-amt-lbl">Amount</div>
                  <span className={`tt-amt ${tx.type === "deposit" ? "tt-amt-dep" : "tt-amt-with"}`}>
                    {tx.type === "deposit" ? "+" : "−"}฿{tx.amount?.toLocaleString()}
                  </span>
                </div>
                <div className="tt-mc-right">
                  <span className="tt-date">{fmtDate(tx.created_date)}</span>
                  <RowTimer createdAt={tx.created_date} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </div>
  );
}